import React, { useCallback, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { Box, Tab, Tabs } from '@mui/material';

import CostPerPointRatesPage from '~/components/Loyalty/CostPerPoint/CostPerPointRatesPage';
import PartnerInvoicePage from '~/components/Loyalty/CostPerPoint/Invoicing/PartnerInvoicePage';
import PartnerTransactionsPage from '~/components/Loyalty/CostPerPoint/Invoicing/PartnerTransactionsPage';
import { currentMonth } from '~/components/Loyalty/CostPerPoint/Invoicing/monthRange';
import PartnerEarnPage from '~/components/Loyalty/PartnerEarn/PartnerEarnPage';
import { loyaltySubTabsContainerSx } from '~/components/Loyalty/loyaltyTabStyles';

import { useCppPartners } from '~/hooks/apis/costPerPoint';

import type { CppPartner } from '~/types/costPerPoint';

import PartnersListPage from './PartnersListPage';

const PARTNERS_SUB_TABS = ['partners', 'rate-cards', 'transactions', 'invoice', 'partner-earn'] as const;

type PartnersSubTab = (typeof PARTNERS_SUB_TABS)[number];

function isPartnersSubTab(value: string | null): value is PartnersSubTab {
  return PARTNERS_SUB_TABS.includes(value as PartnersSubTab);
}

/**
 * Partners page: groups Société Point partner management under a single top-level
 * loyalty tab — a read-only partner directory, the CPP rate-cards workflow, point issuance at
 * transaction level, the monthly partner invoice, and partner earn credit — to reduce nav width.
 *
 * Tab order follows the month-end cycle: who the partner is, what they are charged, what they
 * issued, then the invoice for it. The directory leads because it is the landing tab.
 *
 * The partner and month selection is held here rather than in each tab so that moving between
 * Transactions and Invoice keeps the period you are looking at, which is how the month-end cycle
 * is actually worked: check the issuance detail, then generate the invoice for the same month.
 *
 * The active tab lives in the query string so a tab can be linked to directly — the admin page
 * catalogue lists Partner Earn as `?tab=partner-earn`, and support hand-offs point at a tab rather
 * than at the page plus an instruction to click.
 */
export default function PartnersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: PartnersSubTab = isPartnersSubTab(tabParam) ? tabParam : 'partners';

  const [partnerId, setPartnerId] = useState('');
  const [month, setMonth] = useState(currentMonth);

  const { data: partnersData, isError: hasPartnersError } = useCppPartners();
  const partners: Array<CppPartner> = useMemo(() => partnersData?.result ?? [], [partnersData]);

  const invoicingProps = {
    partners,
    hasPartnersError,
    partnerId,
    month,
    onPartnerChange: setPartnerId,
    onMonthChange: setMonth,
  };

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, next: PartnersSubTab) => {
      setSearchParams(next === 'partners' ? {} : { tab: next }, { replace: true });
    },
    [setSearchParams],
  );

  return (
    <Box>
      <Box sx={loyaltySubTabsContainerSx}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab value="partners" label="Partners" />
          <Tab value="rate-cards" label="Rate Cards" />
          <Tab value="transactions" label="Transactions" />
          <Tab value="invoice" label="Invoice" />
          <Tab value="partner-earn" label="Partner Earn" />
        </Tabs>
      </Box>

      {tab === 'partners' && <PartnersListPage />}
      {tab === 'rate-cards' && <CostPerPointRatesPage />}
      {tab === 'transactions' && <PartnerTransactionsPage {...invoicingProps} />}
      {tab === 'invoice' && <PartnerInvoicePage {...invoicingProps} />}
      {tab === 'partner-earn' && <PartnerEarnPage />}
    </Box>
  );
}
